import React from 'react';
import { Typography, theme } from 'antd';
import { SURFACE_1_SOFT } from '../theme/creamyTokens';

const { Text } = Typography;

/**
 * One figure with its label: the building block of any summary strip.
 *
 * Same card language as BookingMoneyCard/HostDepositPage (container
 * background, hairline border, 16px radius) rather than antd's own
 * `<Statistic>`, whose typography and spacing belong to a different, denser
 * design than this app's.
 *
 * `emphasis` is for the one tile on a screen that is the answer -- it gets
 * the warm fill and the larger figure. Everything else stays plain so the
 * emphasis means something.
 */
export interface IStatTileProps {
  label: string;
  /** Already formatted -- this component never decides how a number reads. */
  value: React.ReactNode;
  /** One short line under the figure: what it excludes, what it is counted from. */
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  emphasis?: boolean;
  /** Overrides the figure's colour, for a tile that is a warning rather than a fact. */
  valueColor?: string;
}

const StatTile: React.FC<IStatTileProps> = ({ label, value, hint, icon, emphasis, valueColor }) => {
  const { token } = theme.useToken();

  return (
    <div
      style={{
        background: emphasis ? SURFACE_1_SOFT : token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 16,
        padding: 16,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: token.colorTextTertiary }}>
        {icon}
        <Text type="secondary" style={{ fontSize: 13 }}>
          {label}
        </Text>
      </div>
      <Text
        strong
        style={{
          // Tabular figures so a column of tiles lines its decimals up
          // instead of shifting with every digit.
          fontVariantNumeric: 'tabular-nums',
          fontSize: emphasis ? 26 : 20,
          lineHeight: 1.2,
          color: valueColor,
        }}
      >
        {value}
      </Text>
      {hint && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {hint}
        </Text>
      )}
    </div>
  );
};

export default StatTile;
